import React, { useState, useCallback, useRef, useMemo } from "react";
import { navigate } from "gatsby-link";
import FormSelect from "./elements/form-select";
import FormText from "./elements/form-text";
import FormPerson from "./elements/form-person";
import FormPrivacy from "./elements/form-privacy";
import { validateEmail, validateTel } from "../utility/formatters";
import { getPrivacyData, submitData } from "../utility/forms";
import { GroupProvider } from "../context/group";
import AltchaWidget from "../utility/altcha";

const initialState = {
  type: "",
  productCategory: "",
  installationType: "",
  firstName: "",
  lastName: "",
  email: "",
  address: "",
  city: "",
  province: "",
  zipCode: "",
  phone: "",
  mobilePhone: "",
  description: "",
};

const Form1 = ({ data, labelManagerLabels }) => {
  const [formData, setFormData] = useState(() => ({
    ...initialState,
    ...getPrivacyData(data),
  }));
  const installationTypeOpts = useMemo(
    () => [
      {
        value: "",
        label: labelManagerLabels.select,
      },
      {
        value: "Built in",
        label: labelManagerLabels.builtIn,
      },
      {
        value: "Freestanding",
        label: labelManagerLabels.freestanding,
      },
    ],
    [labelManagerLabels]
  );
  const requestOpts = useMemo(
    () => [
      {
        value: "",
        label: labelManagerLabels.select,
      },
      {
        value: "Commercial Information",
        label: labelManagerLabels.lookingProductInfo,
      },
      {
        value: "Technical Support request",
        label: labelManagerLabels.formTechnicalSupportRequest,
      },
      {
        value: "How-to-use and Instructions",
        label: labelManagerLabels.formHowtoUseAndInstructions,
      },
      {
        value: "Press office",
        label: labelManagerLabels.contactPressOffice,
      },
      {
        value: "Communications & Advertising",
        label: labelManagerLabels.communicationAdvOffice,
      },
    ],
    [labelManagerLabels]
  );
  const matchIndices = useCallback(
    (value, indices) => {
      return indices.includes(requestOpts.findIndex((item) => item.value === value));
    },
    [requestOpts]
  );
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const altchaRef = useRef();
  const lineOpts = useMemo(
    () =>
      [
        {
          value: "",
          label: labelManagerLabels.select,
        },
      ].concat(data.lines.selectGroups),
    [data, labelManagerLabels]
  );
  const onChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    // console.log('onChange: ', value, checked, type)
    setFormData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
    setFormErrors((current) => (current[name] ? { ...current, [name]: false } : current));
  }, []);

  const formDataRef = useRef({});
  formDataRef.current = formData;

  const formInfo = useMemo(
    () => ({
      formData,
      onChange,
      formErrors,
      setFormErrors,
    }),
    [formData, onChange, formErrors, setFormErrors]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      if (!form.type.value) {
        setFormErrors((current) => ({ ...current, type: true }));
      } else {
        const formElements = [...form.elements]; /* .filter(
          (el) => el.tagName !== "BUTTON" && el.id !== "g-recaptcha-response"
        ); */
        const missingFields = formElements.filter(
          (field) =>
            field.getAttribute &&
            field.getAttribute("data-required") === "true" &&
            (!field.value || (field.type === "checkbox" && !field.checked))
        );

        const invalidFields = [];
        if (form.email?.value && !validateEmail(form.email.value)) {
          invalidFields.push("email");
        }
        if (form.phone?.value && !validateTel(form.phone.value)) {
          invalidFields.push("phone");
        }
        if (form.mobilePhone?.value && !validateTel(form.mobilePhone.value)) {
          invalidFields.push("mobilePhone");
        }

        if (missingFields.length || invalidFields.length) {
          const missingErrs = missingFields.reduce(
            (res, field) => ({
              ...res,
              [field.name]: labelManagerLabels.requiredFields,
            }),
            {}
          );
          const errors = invalidFields.reduce(
            (res, field) => ({ ...res, [field]: true }),
            missingErrs
          );
          setFormErrors((current) => ({ ...current, ...errors }));
          console.warn("Cannot submit: ", errors);
          const firstElError = formElements.find(
            (el) =>
              missingFields.some((field) => el.name === field.name) ||
              invalidFields.includes(el.name)
          );

          var headerOffset =
            document.getElementsByClassName("header")[0].getBoundingClientRect().height + 50;
          var elementPosition = firstElError.getBoundingClientRect().top;
          var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            behavior: "smooth",
            top: offsetPosition,
          });

          return;
        }

        setLoading(true);
        setServerError(false);

        try {
          const result = await submitData(
            { ...formDataRef.current },
            captcha,
            data.privacyText,
            "form1"
          );
          console.log("result: ", result);
          if (data.retUrl) {
            const slug = data.retUrl;
            navigate(slug.startsWith("/") ? slug : `/${slug}`);
          } else {
            navigate("/");
          }
        } catch (err) {
          setServerError(true);
          console.error("Error submitting: ", err);
          altchaRef.current?.reset();
          setCaptcha(null);
        }

        setLoading(false);
      }
    },
    [data.privacyText, data.retUrl, labelManagerLabels, captcha]
  );

  return (
    <section className="section form">
      <div className="container">
        <div className="row">
          <div className="col-12 px-0">
            <div className="form__container">
              <form className="form" onSubmit={onSubmit}>
                <GroupProvider data={formInfo}>
                  <FormSelect
                    id="request"
                    field="type"
                    label={labelManagerLabels.formRequest}
                    options={requestOpts}
                    isRequired={true}
                  />
                  {matchIndices(formData.type, [1]) && (
                    <>
                      <FormSelect
                        id="line"
                        field="productCategory"
                        label={labelManagerLabels.formProductLine}
                        options={lineOpts}
                        isRequired={true}
                      />
                      {formData.productCategory && (
                        <FormSelect
                          id="installationType"
                          field="installationType"
                          label={`${labelManagerLabels.typeOfInstallation}`}
                          options={installationTypeOpts}
                          isRequired={true}
                        />
                      )}
                    </>
                  )}
                  {matchIndices(formData.type, [1, 4, 5]) && (
                    <>
                      <FormPerson labelManagerLabels={labelManagerLabels} />
                      <FormText
                        id="description"
                        field="description"
                        maxLength={32000}
                        placeholder={`${labelManagerLabels.formComments} *`}
                        isRequired={true}
                        rows={10}
                      />

                      <FormPrivacy
                        data={data.privacyText}
                        labelManagerLabels={labelManagerLabels}
                      />
                      {/*Altcha Widget */}
                      <div className="my-5">
                        <AltchaWidget ref={altchaRef} onChange={setCaptcha} />
                      </div>
                      <div className="text-center">
                        <button className="btn-link button primary" disabled={!captcha || loading}>
                          {labelManagerLabels.send}
                        </button>
                        {serverError && (
                          <p className="mt-2 form__service-not-available">
                            {labelManagerLabels.serviceNotAvailable}
                          </p>
                        )}
                      </div>
                    </>
                  )}

                  {matchIndices(formData.type, [2]) && (
                    <div
                      className="form__block p-3 pb-4"
                      dangerouslySetInnerHTML={{ __html: labelManagerLabels.supportAppliance }}
                    ></div>
                  )}

                  {matchIndices(formData.type, [3]) && (
                    <div
                      className="form__block p-3 pb-4"
                      dangerouslySetInnerHTML={{ __html: labelManagerLabels.bookletSearchHaier }}
                    ></div>
                  )}

                  <div className="form__info mt-4">* {labelManagerLabels.requiredFields}</div>
                </GroupProvider>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form1;
